import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Pencil, Plus, Tags } from 'lucide-react';
import type { HolidaySummary, LeaveQuotaPeriod, LeaveTypeSummary } from '@bits/shared/zod';
import { LEAVE_QUOTA_MAX_X10 } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const peopleHolidaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/holidays',
  component: PeopleHolidaysPage,
});

type LeaveTypeSortKey = 'type' | 'quota' | 'pay';
type HolidaySortKey = 'date' | 'holiday';

const EMPTY_TYPES: LeaveTypeSummary[] = [];
const EMPTY_HOLIDAYS: HolidaySummary[] = [];

const LEAVE_TYPE_SORT_ACCESSORS: SortAccessors<LeaveTypeSummary, LeaveTypeSortKey> = {
  type: (t) => t.name,
  quota: (t) => (t.quotaX10 === 0 ? null : t.quotaX10),
  pay: (t) => (t.paid ? 'Paid' : 'Unpaid'),
};

const HOLIDAY_SORT_ACCESSORS: SortAccessors<HolidaySummary, HolidaySortKey> = {
  date: (h) => h.date,
  holiday: (h) => h.name,
};

// Missed clock-ins are counted as loss of pay against this type, so it must
// stay unpaid and available even when nobody would pick it by hand.
const SYSTEM_LOP_TYPE_ID = 'LT_SYSTEM_LOP';

function formatDaysX10(x10: number): string {
  const whole = Math.trunc(x10 / 10);
  const frac = Math.abs(x10 % 10);
  return frac === 0 ? String(whole) : `${whole}.${frac}`;
}

function quotaLabel(type: Pick<LeaveTypeSummary, 'quotaX10' | 'quotaPeriod'>): string {
  if (type.quotaX10 === 0) return 'No limit';
  return `${formatDaysX10(type.quotaX10)} ${type.quotaX10 === 10 ? 'day' : 'days'} / ${type.quotaPeriod}`;
}

function formatHolidayDate(iso: string): string {
  return new Date(`${iso}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type TypeDialog = { mode: 'create' } | { mode: 'edit'; type: LeaveTypeSummary };

function PeopleHolidaysPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);

  const typesQuery = useQuery({ queryKey: ['leave-types'], queryFn: () => api.listLeaveTypes() });
  const holidaysQuery = useQuery({ queryKey: ['holidays'], queryFn: () => api.listHolidays() });

  const refreshTypes = () => queryClient.invalidateQueries({ queryKey: ['leave-types'] });
  const refreshHolidays = () => queryClient.invalidateQueries({ queryKey: ['holidays'] });

  const [typeDialog, setTypeDialog] = React.useState<TypeDialog | null>(null);
  const [typeToToggle, setTypeToToggle] = React.useState<LeaveTypeSummary | null>(null);
  const [holidayOpen, setHolidayOpen] = React.useState(false);
  const [holidayToRemove, setHolidayToRemove] = React.useState<HolidaySummary | null>(null);

  const toggleType = useMutation({
    mutationFn: (t: LeaveTypeSummary) => api.updateLeaveType(t.id, { active: !t.active }),
    onSuccess: async () => {
      setError(null);
      setTypeToToggle(null);
      await refreshTypes();
    },
    onError: (err) => {
      setTypeToToggle(null);
      setError(toMessage(err));
    },
  });

  const removeHoliday = useMutation({
    mutationFn: (h: HolidaySummary) => api.deactivateHoliday(h.id),
    onSuccess: async () => {
      setError(null);
      setHolidayToRemove(null);
      await refreshHolidays();
    },
    onError: (err) => {
      setHolidayToRemove(null);
      setError(toMessage(err));
    },
  });

  const types = typesQuery.data ?? EMPTY_TYPES;
  const activeTypes = types.filter((t) => t.active);
  const inactiveTypes = types.filter((t) => !t.active);
  const orderedTypes = React.useMemo(
    () => [...types].sort((a, b) => Number(b.active) - Number(a.active)),
    [types],
  );
  const { rows: typeRows, ...typeSorter } = useSortedRows(orderedTypes, LEAVE_TYPE_SORT_ACCESSORS);
  const holidays = React.useMemo(
    () =>
      [...(holidaysQuery.data ?? EMPTY_HOLIDAYS)].sort((a, b) => (a.date < b.date ? -1 : 1)),
    [holidaysQuery.data],
  );
  const { rows: holidayRows, ...holidaySorter } = useSortedRows(holidays, HOLIDAY_SORT_ACCESSORS);
  const today = todayIso();
  const upcoming = holidays.filter((h) => h.date >= today).length;

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
          Leave types & holidays
        </h1>
        <p className="max-w-2xl text-sm leading-relaxed text-[hsl(var(--muted-foreground))]">
          Leave types are what a request is filed against. Holidays take a working day out of
          everyone&rsquo;s attendance. Neither is ever deleted: a deactivated type or holiday keeps
          its history and can be brought back.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not save that</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tags className="size-4 text-[hsl(var(--muted-foreground))]" />
                Leave types
              </CardTitle>
              <CardDescription>
                {typesQuery.isPending
                  ? 'Loading…'
                  : `${activeTypes.length} active${
                      inactiveTypes.length > 0 ? ` · ${inactiveTypes.length} inactive` : ''
                    }. Quotas are per person, per year.`}
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setTypeDialog({ mode: 'create' })}>
              <Plus className="size-4" />
              Add type
            </Button>
          </CardHeader>
          <CardContent className="p-0 pb-2">
            {typesQuery.isPending ? null : types.length === 0 ? (
              <EmptyState>No leave types yet. Add one before anyone files a request.</EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))] text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                      <SortableTh sortKey="type" controller={typeSorter} className="px-8 py-2 font-medium">
                        Type
                      </SortableTh>
                      <SortableTh sortKey="quota" controller={typeSorter} className="py-2 pr-4 font-medium">
                        Quota
                      </SortableTh>
                      <SortableTh sortKey="pay" controller={typeSorter} className="py-2 pr-4 font-medium">
                        Pay
                      </SortableTh>
                      <th className="py-2 pr-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {typeRows.map((t) => (
                      <LeaveTypeRow
                        key={t.id}
                        type={t}
                        onEdit={() => setTypeDialog({ mode: 'edit', type: t })}
                        onToggle={() => setTypeToToggle(t)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-1.5">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CalendarDays className="size-4 text-[hsl(var(--muted-foreground))]" />
                Holidays
              </CardTitle>
              <CardDescription>
                {holidaysQuery.isPending
                  ? 'Loading…'
                  : `${holidays.length} on the calendar · ${upcoming} still to come.`}
              </CardDescription>
            </div>
            <Button size="sm" onClick={() => setHolidayOpen(true)}>
              <Plus className="size-4" />
              Add holiday
            </Button>
          </CardHeader>
          <CardContent className="p-0 pb-2">
            {holidaysQuery.isPending ? null : holidays.length === 0 ? (
              <EmptyState>No holidays on the calendar.</EmptyState>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))] text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                      <SortableTh sortKey="date" controller={holidaySorter} className="px-8 py-2 font-medium">
                        Date
                      </SortableTh>
                      <SortableTh sortKey="holiday" controller={holidaySorter} className="py-2 pr-4 font-medium">
                        Holiday
                      </SortableTh>
                      <th className="py-2 pr-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {holidayRows.map((h) => {
                      const past = h.date < today;
                      return (
                        <tr
                          key={h.id}
                          className={cn(
                            'border-b border-[hsl(var(--border))] last:border-b-0 hover:bg-[hsl(var(--muted))]/40',
                            past && 'text-[hsl(var(--muted-foreground))]',
                          )}
                        >
                          <td className="whitespace-nowrap px-8 py-3 tabular-nums">
                            {formatHolidayDate(h.date)}
                          </td>
                          <td className={cn('py-3 pr-4', !past && 'font-medium')}>
                            {h.name}
                            {h.date === today ? (
                              <Badge variant="default" className="ml-2">
                                Today
                              </Badge>
                            ) : null}
                          </td>
                          <td className="py-3 pr-8 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[hsl(var(--destructive))]"
                              onClick={() => setHolidayToRemove(h)}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <LeaveTypeDialog
        state={typeDialog}
        existing={types}
        onClose={() => setTypeDialog(null)}
        onSaved={async () => {
          setTypeDialog(null);
          setError(null);
          await refreshTypes();
        }}
      />

      <Dialog
        open={typeToToggle !== null}
        onOpenChange={(open) => (!open ? setTypeToToggle(null) : undefined)}
      >
        <DialogContent className="max-w-md">
          {typeToToggle ? (
            <>
              <DialogHeader>
                <DialogTitle>
                  {typeToToggle.active ? 'Deactivate' : 'Reactivate'} {typeToToggle.name}?
                </DialogTitle>
                <DialogDescription>
                  {typeToToggle.active
                    ? 'It disappears from the leave request form. Requests already filed against it are untouched, and you can reactivate it at any time.'
                    : 'It becomes available in the leave request form again.'}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setTypeToToggle(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant={typeToToggle.active ? 'destructive' : 'default'}
                  disabled={toggleType.isPending}
                  onClick={() => toggleType.mutate(typeToToggle)}
                >
                  {toggleType.isPending
                    ? 'Saving…'
                    : typeToToggle.active
                      ? 'Deactivate'
                      : 'Reactivate'}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <HolidayDialog
        open={holidayOpen}
        onClose={() => setHolidayOpen(false)}
        onSaved={async () => {
          setHolidayOpen(false);
          setError(null);
          await refreshHolidays();
        }}
      />

      <Dialog
        open={holidayToRemove !== null}
        onOpenChange={(open) => (!open ? setHolidayToRemove(null) : undefined)}
      >
        <DialogContent className="max-w-md">
          {holidayToRemove ? (
            <>
              <DialogHeader>
                <DialogTitle>Remove {holidayToRemove.name}?</DialogTitle>
                <DialogDescription>
                  {formatHolidayDate(holidayToRemove.date)} becomes an ordinary working day again
                  for everyone. The record is kept, not deleted.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setHolidayToRemove(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={removeHoliday.isPending}
                  onClick={() => removeHoliday.mutate(holidayToRemove)}
                >
                  {removeHoliday.isPending ? 'Removing…' : 'Remove'}
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function LeaveTypeRow({
  type,
  onEdit,
  onToggle,
}: {
  type: LeaveTypeSummary;
  onEdit: () => void;
  onToggle: () => void;
}): JSX.Element {
  const isSystem = type.id === SYSTEM_LOP_TYPE_ID;
  return (
    <tr
      className={cn(
        'border-b border-[hsl(var(--border))] last:border-b-0 hover:bg-[hsl(var(--muted))]/40',
        !type.active && 'text-[hsl(var(--muted-foreground))]',
      )}
    >
      <td className="px-8 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'whitespace-nowrap',
              type.active && 'font-medium text-[hsl(var(--foreground))]',
            )}
          >
            {type.name}
          </span>
          {isSystem ? (
            <Badge
              variant="outline"
              title="Missed clock-ins counted as loss of pay are recorded against this type"
            >
              System
            </Badge>
          ) : null}
          {!type.active ? <Badge variant="muted">Inactive</Badge> : null}
        </div>
      </td>
      <td className="whitespace-nowrap py-3 pr-4 tabular-nums">{quotaLabel(type)}</td>
      <td className="py-3 pr-4">
        <Badge variant={type.paid ? 'secondary' : 'destructive'}>
          {type.paid ? 'Paid' : 'Unpaid'}
        </Badge>
      </td>
      <td className="py-3 pr-8">
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label={`Edit ${type.name}`}>
            <Pencil className="size-3.5" />
            Edit
          </Button>
          {isSystem ? null : (
            <Button
              variant="ghost"
              size="sm"
              className={cn(type.active && 'text-[hsl(var(--destructive))]')}
              onClick={onToggle}
            >
              {type.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function LeaveTypeDialog({
  state,
  existing,
  onClose,
  onSaved,
}: {
  state: TypeDialog | null;
  existing: LeaveTypeSummary[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = React.useState('');
  const [quotaDays, setQuotaDays] = React.useState('');
  const [quotaPeriod, setQuotaPeriod] = React.useState<LeaveQuotaPeriod>('year');
  const [paid, setPaid] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const editing = state?.mode === 'edit' ? state.type : null;
  const isSystem = editing?.id === SYSTEM_LOP_TYPE_ID;

  React.useEffect(() => {
    if (!state) return;
    setError(null);
    if (state.mode === 'edit') {
      setName(state.type.name);
      setQuotaDays(state.type.quotaX10 === 0 ? '' : formatDaysX10(state.type.quotaX10));
      setQuotaPeriod(state.type.quotaPeriod);
      setPaid(state.type.paid);
    } else {
      setName('');
      setQuotaDays('');
      setQuotaPeriod('year');
      setPaid(true);
    }
  }, [state]);

  const trimmed = name.trim();
  const quotaX10 = quotaDays.trim() === '' ? 0 : Math.round(parseFloat(quotaDays) * 10);
  const quotaMaxDays = LEAVE_QUOTA_MAX_X10[quotaPeriod] / 10;
  const quotaValid =
    Number.isFinite(quotaX10) && quotaX10 >= 0 && quotaX10 <= LEAVE_QUOTA_MAX_X10[quotaPeriod];
  const nameTaken = existing.some(
    (t) => t.id !== editing?.id && t.name.toLowerCase() === trimmed.toLowerCase(),
  );
  const unchanged =
    editing !== null &&
    trimmed === editing.name &&
    quotaX10 === editing.quotaX10 &&
    quotaPeriod === editing.quotaPeriod &&
    paid === editing.paid;

  const save = useMutation({
    mutationFn: () => {
      if (editing) {
        return api.updateLeaveType(editing.id, {
          ...(trimmed !== editing.name ? { name: trimmed } : {}),
          ...(quotaX10 !== editing.quotaX10 ? { quotaX10 } : {}),
          ...(quotaPeriod !== editing.quotaPeriod ? { quotaPeriod } : {}),
          ...(paid !== editing.paid ? { paid } : {}),
        });
      }
      return api.createLeaveType({ name: trimmed, paid, quotaX10, quotaPeriod });
    },
    onSuccess: () => onSaved(),
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <Dialog open={state !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add leave type'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Changes apply to new requests. Requests already filed keep the days they were granted.'
              : 'Unpaid leave counts as loss of pay and reduces paid days for the month.'}
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (trimmed === '' || nameTaken || !quotaValid || unchanged) return;
            save.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lt-name">Name</Label>
            <Input
              id="lt-name"
              autoFocus
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Casual Leave"
            />
            {nameTaken ? (
              <p className="text-xs text-[hsl(var(--destructive))]">
                A leave type with this name already exists.
              </p>
            ) : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lt-quota">Quota</Label>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Input
                id="lt-quota"
                type="number"
                step="0.5"
                min={0}
                max={quotaMaxDays}
                value={quotaDays}
                onChange={(e) => setQuotaDays(e.currentTarget.value)}
                placeholder="Days, blank for no limit"
              />
              <Select
                aria-label="Quota period"
                value={quotaPeriod}
                onChange={(e) => setQuotaPeriod(e.currentTarget.value as LeaveQuotaPeriod)}
              >
                <option value="year">per year</option>
                <option value="month">per month</option>
              </Select>
            </div>
            {!quotaValid ? (
              <p className="text-xs text-[hsl(var(--destructive))]">
                Enter a number of days between 0 and {quotaMaxDays}.
              </p>
            ) : (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                {quotaPeriod === 'month'
                  ? 'The allowance resets every month.'
                  : 'The allowance resets every year.'}
              </p>
            )}
          </div>
          <label
            className={cn(
              'flex items-center gap-2 text-sm',
              isSystem && 'text-[hsl(var(--muted-foreground))]',
            )}
          >
            <input
              type="checkbox"
              className="size-4 accent-[hsl(var(--brand))]"
              checked={paid}
              disabled={isSystem}
              onChange={(e) => setPaid(e.currentTarget.checked)}
            />
            Paid leave
          </label>
          {isSystem ? (
            <p className="-mt-2 text-xs text-[hsl(var(--muted-foreground))]">
              This type records missed clock-ins as loss of pay, so it stays unpaid.
            </p>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Could not save</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={trimmed === '' || nameTaken || !quotaValid || unchanged || save.isPending}
            >
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Add type'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HolidayDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [date, setDate] = React.useState('');
  const [name, setName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDate('');
      setName('');
      setError(null);
    }
  }, [open]);

  const save = useMutation({
    mutationFn: () => api.createHoliday({ date, name: name.trim() }),
    onSuccess: () => onSaved(),
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add holiday</DialogTitle>
          <DialogDescription>
            A holiday takes that working day out of everyone&rsquo;s attendance.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (date === '' || name.trim() === '') return;
            save.mutate();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hol-date">Date</Label>
            <Input
              id="hol-date"
              type="date"
              autoFocus
              value={date}
              onChange={(e) => setDate(e.currentTarget.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hol-name">Name</Label>
            <Input
              id="hol-name"
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Republic Day"
            />
          </div>
          {error ? (
            <Alert variant="destructive">
              <AlertTitle>Could not add</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={date === '' || name.trim() === '' || save.isPending}>
              {save.isPending ? 'Adding…' : 'Add holiday'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="mx-8 mb-6 rounded-xl border border-dashed border-[hsl(var(--border))] py-10 text-center">
      <p className="text-sm text-[hsl(var(--muted-foreground))]">{children}</p>
    </div>
  );
}
